import { format, parseISO } from 'date-fns';

/**
 * Formats a UTC date string to the user's local time string.
 * @param {string} dateString - The UTC date string (e.g., ISO 8601).
 * @param {string} formatStr - The format string (default: 'MMM d, h:mm a').
 * @returns {string} - The formatted local date string.
 */
export const formatDateToLocal = (dateInput, formatStr = 'MMM d, h:mm a') => {
  if (!dateInput) return 'Unknown Date';
  try {
    let date;
    
    // Parse timestamp based on input type
    if (typeof dateInput === 'string') {
      // Check if it's a numeric string (timestamp in seconds or milliseconds)
      if (/^\d+(\.\d+)?$/.test(dateInput)) {
        const num = parseFloat(dateInput);
        date = new Date(num > 1e11 ? num : num * 1000); 
      } else {
        // Since we migrated the DB to TIMESTAMP WITH TIME ZONE (TIMESTAMPTZ),
        // the backend now serves standard ISO strings (e.g., 2026-03-05T04:24:23.000000+00:00).
        // The standard JS Date parser natively converts these exact UTC times to the user's local timezone.
        date = new Date(dateInput);
      }
    } else if (typeof dateInput === 'number') {
       date = new Date(dateInput > 1e11 ? dateInput : dateInput * 1000);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      console.warn('Invalid date input type:', typeof dateInput, dateInput);
      return 'Unknown Date';
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date created from input:', dateInput, 'resulting date:', date);
      return 'Unknown Date';
    }
    
    // Uses date-fns to format the user's localized Date object nicely
    return format(date, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error, "Input:", dateInput);
    return 'Unknown Date';
  }
};

/**
 * Single app-wide relative-time formatter (1.7). Rolls seconds up into minutes,
 * hours, days, and weeks so no surface ever prints a raw hour count above ~48
 * (e.g. "595h ago" becomes "3w ago"). Accepts an ISO string, epoch number, or
 * Date. Returns "" for missing/invalid input so callers can guard easily.
 *
 * @param {string|number|Date} input
 * @param {object} [opts]
 * @param {boolean} [opts.suffix=true]  append " ago"
 * @returns {string}
 */
export const formatRelativeTime = (input, { suffix = true } = {}) => {
  if (input === null || input === undefined || input === "") return "";
  let date;
  if (input instanceof Date) date = input;
  else if (typeof input === "number") date = new Date(input > 1e11 ? input : input * 1000);
  else if (typeof input === "string") {
    if (/^\d+(\.\d+)?$/.test(input)) {
      const n = parseFloat(input);
      date = new Date(n > 1e11 ? n : n * 1000);
    } else {
      date = new Date(input);
    }
  }
  if (!date || isNaN(date.getTime())) return "";

  const diffSec = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  const tail = suffix ? " ago" : "";

  if (diffSec < 45) return `just now`;
  const min = Math.round(diffSec / 60);
  if (diffSec < 3600) return `${min}m${tail}`;
  const hours = Math.round(diffSec / 3600);
  if (diffSec < 86400) return `${hours}h${tail}`;          // < 24h → hours
  const days = Math.round(diffSec / 86400);
  if (days < 7) return `${days}d${tail}`;                  // < 1w → days
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}w${tail}`;                // < ~5w → weeks
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo${tail}`;
  const years = Math.round(days / 365);
  return `${years}y${tail}`;
};

/**
 * Returns the current date and time as a UTC ISO string.
 * @returns {string} - The current date/time in UTC ISO format.
 */
export const getCurrentUTCDate = () => {
  const now = new Date();
  // Create UTC date by getting the UTC components
  const utcYear = now.getUTCFullYear();
  const utcMonth = now.getUTCMonth();
  const utcDay = now.getUTCDate();
  const utcHours = now.getUTCHours();
  const utcMinutes = now.getUTCMinutes();
  const utcSeconds = now.getUTCSeconds();
  const utcMilliseconds = now.getUTCMilliseconds();
  
  // Create a proper UTC Date object
  const utcDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, utcHours, utcMinutes, utcSeconds, utcMilliseconds));
  return utcDate.toISOString();
};

/**
 * Converts a local Date object to a UTC ISO string.
 * @param {Date} date - The local date object.
 * @returns {string} - The UTC ISO string.
 */
export const convertLocalToUTC = (date) => {
    if (!date) return null;
    return date.toISOString();
}
